// Stat modifier pipeline.
//
// Every system that boosts the player (relics now; artifacts, equipment, pets,
// skill tree later) registers a ModifierSource. Stats are recomputed only when
// something is marked dirty, so per-frame reads are cheap even with many sources.
import { D, Decimal, ONE } from '../core/decimal';
import type { HeroType } from '../data/heroes';
import type { GameState } from './state';

/** Multiplicative stats (Decimal: artifact stacks can get astronomically large). */
export type MultStat =
  | 'allDamage' | 'tapDamage' | 'heroDamage'
  | `heroDamage.${HeroType}`
  | `heroSelf.${string}` // one specific hero (by id)
  | 'critDamage' | 'bossDamage'
  | 'gold' | 'bossGold' | 'relicGain'
  | 'heroCost' | 'tapCost'
  | 'strikeDamage' | 'midasGold';

/** Additive stats (plain numbers: chances, seconds, shares, fractions). */
export type AddStat =
  | 'critChance' | 'tapDpsShare' | 'bossTimeSec' | 'offlineEfficiency'
  | 'manaRegenPct' | 'skillDurationPct' | 'warcryBonus' | 'frenzyRate'
  | 'maxMana' | 'fireswordBonus';

export interface Modifiers {
  mult(stat: MultStat, value: Decimal | number): void;
  add(stat: AddStat, value: number): void;
}

export interface ModifierSource {
  id: string;
  apply(state: GameState, m: Modifiers): void;
}

export class Stats {
  private sources: ModifierSource[] = [];
  private mults = new Map<MultStat, Decimal>();
  private adds = new Map<AddStat, number>();
  private dirty = true;

  constructor(private getState: () => GameState) {}

  register(source: ModifierSource): void {
    this.sources.push(source);
    this.dirty = true;
  }

  /** Call after anything a source reads changes (purchases, prestige, equips...). */
  invalidate(): void {
    this.dirty = true;
  }

  mult(stat: MultStat): Decimal {
    this.refresh();
    return this.mults.get(stat) ?? ONE;
  }

  add(stat: AddStat): number {
    this.refresh();
    return this.adds.get(stat) ?? 0;
  }

  /** Per-source breakdown, for a future stats screen and for debugging balance. */
  breakdown(): Array<{ id: string; mults: [MultStat, string][]; adds: [AddStat, number][] }> {
    return this.sources.map((src) => {
      const mults: [MultStat, string][] = [], adds: [AddStat, number][] = [];
      src.apply(this.getState(), {
        mult: (s, v) => mults.push([s, D(v).toString()]),
        add: (s, v) => adds.push([s, v]),
      });
      return { id: src.id, mults, adds };
    });
  }

  private refresh(): void {
    if (!this.dirty) return;
    this.dirty = false;
    this.mults.clear();
    this.adds.clear();
    const state = this.getState();
    const m: Modifiers = {
      mult: (s, v) => this.mults.set(s, (this.mults.get(s) ?? ONE).mul(v)),
      add: (s, v) => this.adds.set(s, (this.adds.get(s) ?? 0) + v),
    };
    for (const src of this.sources) src.apply(state, m);
  }
}
